import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { TierGate } from "@/components/TierGate";
import { ImageDropZone } from "@/components/ImageDropZone";
import { SlidePanel } from "@/components/SlidePanel";
import { RelatedEntitiesPanel } from "@/components/RelatedEntitiesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Search, Sword, Shield, FlaskConical, Scroll, Gem, Package, MapPin, User, Crown, Upload, BookOpen, Check, Sparkles, Lock, ShieldCheck, ShieldOff, Wand2, CheckSquare, Users, Coins, Weight } from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import AICreateMode from "@/components/AICreateMode";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { useMultiSelect, BulkActionBar, SelectCheckbox } from "@/components/MultiSelect";
import EntityContextMenu from "@/components/EntityContextMenu";
import { FolderColorStrip } from "@/components/FolderColorStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_TYPES = [
  { value: "weapon", label: "Weapon", icon: Sword },
  { value: "armour", label: "Armour", icon: Shield },
  { value: "potion", label: "Potion", icon: FlaskConical },
  { value: "scroll", label: "Scroll", icon: Scroll },
  { value: "gem", label: "Gem/Jewellery", icon: Gem },
  { value: "artifact", label: "Artifact", icon: Crown },
  { value: "misc", label: "Miscellaneous", icon: Package },
];

const RARITIES = [
  { value: "common", label: "Common", color: "text-stone-400" },
  { value: "uncommon", label: "Uncommon", color: "text-green-400" },
  { value: "rare", label: "Rare", color: "text-blue-400" },
  { value: "very_rare", label: "Very Rare", color: "text-violet-400" },
  { value: "legendary", label: "Legendary", color: "text-amber-400" },
];

const getRarityConfig = (r) => RARITIES.find(x => x.value === r) || RARITIES[0];
const getItemTypeConfig = (t) => ITEM_TYPES.find(x => x.value === t) || ITEM_TYPES[5];

const SRD_ITEM_TYPES = [
  { value: "weapon", label: "Weapon" },
  { value: "armor", label: "Armor" },
  { value: "gear", label: "Gear" },
  { value: "potion", label: "Potion" },
  { value: "wondrous", label: "Wondrous Item" },
  { value: "ring", label: "Ring" },
  { value: "artifact", label: "Artifact" },
];

const ItemsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userTier = user?.tier || "commoner";
  const hasSrdAccess = userTier !== "commoner";
  const isCommoner = userTier === "commoner";
  const [campaign, setCampaign] = useState(null);
  const [items, setItems] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createMode, setCreateMode] = useState("manual");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterRarity, setFilterRarity] = useState("all");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState("all"); // legacy — kept for FolderColorStrip compat
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "items", onRefresh: () => fetchData() });
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);
  const multi = useMultiSelect();
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    name: "", description: "", item_type: "misc", rarity: "common",
    character_id: "", location_id: "", properties: "",
    quantity: 1, weight: 0, in_party_loot: false,
    folder_id: ""
  });

  // SRD Items Library state
  const [activeTab, setActiveTab] = useState("inventory");
  const [partyLoot, setPartyLoot] = useState(null); // { currency, notes, items[], total_weight }
  const [partyLootLoading, setPartyLootLoading] = useState(false);

  const fetchPartyLoot = async () => {
    setPartyLootLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/party-loot`, { withCredentials: true });
      setPartyLoot(res.data);
    } catch { toast.error("Failed to load party loot"); }
    finally { setPartyLootLoading(false); }
  };
  const updatePartyCurrency = async (coins) => {
    try {
      const res = await axios.put(`${API}/campaigns/${campaignId}/party-loot/currency`, coins, { withCredentials: true });
      setPartyLoot(p => p ? { ...p, currency: res.data.currency } : p);
    } catch { toast.error("Failed to save currency"); }
  };
  const updatePartyNotes = async (notes) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/party-loot/notes`, { notes }, { withCredentials: true });
      setPartyLoot(p => p ? { ...p, notes } : p);
    } catch { toast.error("Failed to save notes"); }
  };
  const togglePartyLootForItem = async (item) => {
    try {
      await axios.post(`${API}/items/${item.id}/move-to-party`, {}, { withCredentials: true });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, in_party_loot: !i.in_party_loot, character_id: !i.in_party_loot ? null : i.character_id } : i));
      if (activeTab === "party") fetchPartyLoot();
      toast.success(item.in_party_loot ? `Pulled "${item.name}" from party loot` : `Sent "${item.name}" to party loot`);
    } catch { toast.error("Failed"); }
  };
  const assignPartyItemToCharacter = async (itemId, characterId) => {
    try {
      await axios.post(`${API}/items/${itemId}/assign-to-character`, { character_id: characterId }, { withCredentials: true });
      const charName = characters.find(c => c.id === characterId)?.name || "character";
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, in_party_loot: false, character_id: characterId } : i));
      fetchPartyLoot();
      toast.success(`Assigned to ${charName}`);
    } catch { toast.error("Failed to assign"); }
  };
  const [srdItems, setSrdItems] = useState([]);
  const [srdSearch, setSrdSearch] = useState("");
  const [srdFilterType, setSrdFilterType] = useState("all");
  const [srdFilterRarity, setSrdFilterRarity] = useState("all");
  const [srdFilterMagic, setSrdFilterMagic] = useState("all");
  const [srdLoading, setSrdLoading] = useState(false);
  const [addingItem, setAddingItem] = useState(null);
  const [addedItems, setAddedItems] = useState(new Set());

  useEffect(() => { fetchData(); }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campRes, itemsRes, charsRes, locsRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/items`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
      ]);
      setCampaign(campRes.data);
      setItems(itemsRes.data);
      setCharacters(charsRes.data);
      setLocations(locsRes.data);
      folderCrud.refresh();
    } catch { toast.error("Failed to load items"); }
    finally { setLoading(false); }
  };

  const fetchSrdItems = async () => {
    if (srdItems.length > 0) return;
    setSrdLoading(true);
    try {
      const res = await axios.get(`${API}/srd-items`, { withCredentials: true });
      setSrdItems(res.data);
    } catch { /* TierGate handles display */ }
    finally { setSrdLoading(false); }
  };

  const addSrdItem = async (srdItem) => {
    setAddingItem(srdItem.id);
    try {
      await axios.post(`${API}/srd-items/add-to-campaign`, { campaign_id: campaignId, srd_id: srdItem.id }, { withCredentials: true });
      toast.success(`${srdItem.name} added to your inventory`);
      setAddedItems(prev => new Set(prev).add(srdItem.id));
      fetchData();
    } catch { toast.error("Failed to add item"); }
    finally { setAddingItem(null); }
  };

  const toggleEquip = async (item) => {
    try {
      const res = await axios.put(`${API}/items/${item.id}/equip`, {}, { withCredentials: true });
      const verb = res.data.equipped ? "equipped" : "unequipped";
      toast.success(`${item.name} ${verb}`);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, equipped: res.data.equipped } : i));
    } catch { toast.error("Failed to update item"); }
  };

  const filteredSrd = srdItems.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(srdSearch.toLowerCase());
    const matchType = srdFilterType === "all" || i.item_type === srdFilterType;
    const matchRarity = srdFilterRarity === "all" || i.rarity === srdFilterRarity;
    const matchMagic = srdFilterMagic === "all" || (srdFilterMagic === "magical" ? i.magical : !i.magical);
    return matchSearch && matchType && matchRarity && matchMagic;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        campaign_id: campaignId,
        character_id: formData.character_id === "none" ? null : formData.character_id || null,
        location_id: formData.location_id === "none" ? null : formData.location_id || null,
        folder_id: formData.folder_id === "none" || !formData.folder_id ? null : formData.folder_id,
      };
      let itemId;
      if (editing) {
        await axios.put(`${API}/items/${editing.id}`, payload, { withCredentials: true });
        itemId = editing.id;
        toast.success("Item updated");
      } else {
        const res = await axios.post(`${API}/items`, payload, { withCredentials: true });
        itemId = res.data.id;
        toast.success("Item created");
      }
      if (imageFile && itemId) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await axios.post(`${API}/items/${itemId}/upload`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save item"); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Item", "Are you sure you want to delete this item?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/items/${id}`, { withCredentials: true });
      toast.success("Item deleted");
      fetchData();
    } catch { toast.error("Failed to delete item"); }
  };

  const resetForm = () => {
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({ name: "", description: "", item_type: "misc", rarity: "common", character_id: "", location_id: "", properties: "", quantity: 1, weight: 0, in_party_loot: false, folder_id: folderCrud.currentFolderId || "" });
  };

  const handleFormImageSelect = (file) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const openEdit = (item) => {
    setEditing(item);
    setImageFile(null);
    setImagePreview(item.image_path ? `${API}/files/${item.image_path}` : null);
    setFormData({
      name: item.name, description: item.description || "",
      item_type: item.item_type || "misc", rarity: item.rarity || "common",
      character_id: item.character_id || "", location_id: item.location_id || "",
      properties: item.properties || "",
      quantity: item.quantity ?? 1, weight: item.weight ?? 0, in_party_loot: !!item.in_party_loot,
      folder_id: item.folder_id || ""
    });
    setDialogOpen(true);
  };

  const getCharName = (id) => characters.find(c => c.id === id)?.name || "";
  const getLocName = (id) => locations.find(l => l.id === id)?.name || "";

  const filtered = items.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === "all" || i.item_type === filterType;
    const matchRarity = filterRarity === "all" || i.rarity === filterRarity;
    return matchSearch && matchType && matchRarity;
  });
  const { folders: levelFolders, items: filteredByFolderItems } = partitionCurrentLevel({
    folders, items: filtered, currentFolderId: folderCrud.currentFolderId
  });

  const moveItemToFolder = async (itemId, folderId) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/items/${itemId}/folder`, { folder_id: folderId }, { withCredentials: true });
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, folder_id: folderId } : i));
    } catch { toast.error("Failed to move item"); }
  };

  const generateItemImage = async (itemId) => {
    const item = items.find(x => x.id === itemId);
    if (!item) return;
    try {
      toast.info("Generating AI image...");
      await axios.post(`${API}/items/${itemId}/generate-image`, { prompt: `D&D fantasy item illustration of ${item.name}, ${item.item_type || ""} ${item.rarity || ""}, ${(item.description || "").substring(0, 200)}, dark fantasy equipment art style, detailed, atmospheric, no text` }, { withCredentials: true });
      toast.success("Image generating — will appear shortly");
      setTimeout(fetchItems, 5000);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate image"); }
  };

  const bulkMoveItems = async (folderId) => {
    const ids = [...multi.selectedIds];
    try {
      await Promise.all(ids.map(id => axios.put(`${API}/campaigns/${campaignId}/items/${id}/folder`, { folder_id: folderId }, { withCredentials: true })));
      setItems(prev => prev.map(i => ids.includes(i.id) ? { ...i, folder_id: folderId } : i));
      multi.clearAll();
      toast.success(`Moved ${ids.length} items`);
    } catch { toast.error("Failed"); }
  };

  const bulkDeleteItems = async () => {
    const ids = [...multi.selectedIds];
    if (!window.confirm(`Delete ${ids.length} items?`)) return;
    try {
      await Promise.all(ids.map(id => axios.delete(`${API}/items/${id}`, { withCredentials: true })));
      setItems(prev => prev.filter(i => !ids.includes(i.id)));
      multi.clearAll();
      toast.success(`Deleted ${ids.length} items`);
    } catch { toast.error("Failed"); }
  };

  if (loading) return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner" /></div></Layout>;

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="items-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Items</h1>
            <p className="text-muted-foreground text-sm">Weapons, armour, potions, and treasures</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={multi.selectMode ? "default" : "outline"} className="rounded-sm text-xs" onClick={() => multi.setSelectMode(!multi.selectMode)} data-testid="toggle-select-mode">
              <CheckSquare className="w-3.5 h-3.5 mr-1" /> {multi.selectMode ? "Cancel" : "Select"}
            </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setCreateMode("manual"); setDialogOpen(true); }} data-testid="create-item-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> New Item
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{editing ? "Edit Item" : "New Item"}</DialogTitle>
              </DialogHeader>
              {!editing && (
                <div className="flex gap-2 border-b border-border pb-3 mb-1">
                  <Button variant={createMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("manual")} data-testid="mode-manual">
                    <Plus className="w-3 h-3 mr-1" /> Create Manually
                  </Button>
                  {!isCommoner && (
                    <Button variant={createMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("ai")} data-testid="mode-ai">
                      <Wand2 className="w-3 h-3 mr-1" /> Use AI
                    </Button>
                  )}
                  {isCommoner && (
                    <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled data-testid="mode-ai-locked">
                      <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                    </Button>
                  )}
                </div>
              )}
              {!editing && createMode === "ai" && (
                <AICreateMode entityType="item" campaignId={campaignId} onGenerated={(data) => {
                  setFormData(prev => ({ ...prev, ...data }));
                  setCreateMode("manual");
                }} />
              )}
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" style={{ display: (!editing && createMode === "ai") ? "none" : undefined }}>
                <div>
                  <label className="text-sm font-medium block mb-2">Item Image</label>
                  <ImageDropZone
                    compact
                    onFileSelect={handleFormImageSelect}
                    preview={imagePreview}
                    fileName={imageFile?.name}
                    testId="item-image-picker"
                    onClear={() => { setImageFile(null); setImagePreview(null); }}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Name *</label>
                  <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Flame Tongue Longsword" required data-testid="item-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Type</label>
                    <Select value={formData.item_type} onValueChange={v => setFormData({...formData, item_type: v})}>
                      <SelectTrigger data-testid="item-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Rarity</label>
                    <Select value={formData.rarity} onValueChange={v => setFormData({...formData, rarity: v})}>
                      <SelectTrigger data-testid="item-rarity-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{RARITIES.map(r => <SelectItem key={r.value} value={r.value}><span className={r.color}>{r.label}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Held By (Character)</label>
                  <Select value={formData.character_id || "none"} onValueChange={v => setFormData({...formData, character_id: v})}>
                    <SelectTrigger data-testid="item-character-select"><SelectValue placeholder="No one" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No one</SelectItem>
                      {characters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Found At (Location)</label>
                  <Select value={formData.location_id || "none"} onValueChange={v => setFormData({...formData, location_id: v})}>
                    <SelectTrigger data-testid="item-location-select"><SelectValue placeholder="Unknown" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unknown</SelectItem>
                      {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium block mb-1">Quantity</label>
                    <Input type="number" min="1" value={formData.quantity} onChange={e => setFormData({...formData, quantity: parseInt(e.target.value) || 1})} data-testid="item-quantity-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Weight (lb each)</label>
                    <Input type="number" min="0" step="0.1" value={formData.weight} onChange={e => setFormData({...formData, weight: parseFloat(e.target.value) || 0})} data-testid="item-weight-input" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="item-party-loot-toggle">
                  <input type="checkbox" checked={!!formData.in_party_loot} onChange={e => setFormData({...formData, in_party_loot: e.target.checked, character_id: e.target.checked ? "" : formData.character_id})} className="accent-primary w-4 h-4" />
                  <span>Add to <span className="text-primary font-medium">Party Loot Pool</span> (shared between all party members)</span>
                </label>
                <div>
                  <label className="text-sm font-medium block mb-1">Folder</label>
                  <Select value={formData.folder_id || "none"} onValueChange={v => setFormData({...formData, folder_id: v === "none" ? "" : v})}>
                    <SelectTrigger data-testid="item-folder-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unfiled</SelectItem>
                      {folders.filter(f => !f.archived).sort((a, b) => a.name.localeCompare(b.name)).map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Properties</label>
                  <Input value={formData.properties} onChange={e => setFormData({...formData, properties: e.target.value})} placeholder="1d8 slashing, +2 fire damage, requires attunement" data-testid="item-properties-input" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Description</label>
                  <RichTextEditor value={formData.description} onChange={v => setFormData({...formData, description: v})} campaignId={campaignId} />
                </div>
                <Button type="submit" className="w-full" data-testid="save-item-button">{editing ? "Save Changes" : "Create Item"}</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "library") fetchSrdItems(); if (v === "party") fetchPartyLoot(); }} className="w-full">
          <TabsList className="bg-muted mb-6">
            <TabsTrigger value="inventory" data-testid="tab-my-items">My Items</TabsTrigger>
            <TabsTrigger value="party" data-testid="tab-party-loot">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Party Loot {partyLoot && (partyLoot.items?.length || 0) > 0 && <span className="ml-1.5 text-[10px] text-primary">({partyLoot.items.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="library" data-testid="tab-items-library">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Items Library
              {!hasSrdAccess && <Lock className="w-3 h-3 ml-1.5 text-muted-foreground" />}
            </TabsTrigger>
          </TabsList>

          {/* ==================== MY ITEMS TAB ==================== */}
          <TabsContent value="inventory">
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="item-search" />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40" data-testid="item-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterRarity} onValueChange={setFilterRarity}>
                <SelectTrigger className="w-40" data-testid="item-filter-rarity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rarities</SelectItem>
                  {RARITIES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Folder browser */}
            <div className="flex gap-4 items-start">
              <FolderRail folders={folders} items={items} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} allLabel="All Items" className="w-44 shrink-0" testIdPrefix="items" />
              <div className="flex-1 min-w-0">
                <FolderToolbar folders={folders} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} onNewFolder={folderCrud.createHere} allLabel="All Items" className="mb-3" />
                {levelFolders.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 mb-4" data-testid="items-folder-grid">
                    {levelFolders.map(f => (
                      <FolderCard key={f.id} folder={f}
                        count={items.filter(i => i.folder_id === f.id).length + folders.filter(x => x.parent_id === f.id && !x.archived).length}
                        onOpen={() => folderCrud.setCurrentFolderId(f.id)}
                        onRename={() => folderCrud.renameFolder(f)}
                        onDelete={() => folderCrud.deleteFolder(f)}
                        onArchive={() => folderCrud.toggleArchive(f)}
                        onColor={(c) => folderCrud.setFolderColor(f, c)}
                        onDropItem={folderCrud.moveItem} />
                    ))}
                  </div>
                )}

            {filteredByFolderItems.length === 0 && levelFolders.length === 0 ? (
              <div className="empty-state">
                <Package className="empty-state-icon text-muted-foreground w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-serif text-foreground mb-2">{items.length === 0 ? "No items yet" : "No matches"}</h3>
                <p className="text-muted-foreground mb-6">{items.length === 0 ? "Add weapons, potions, and treasures" : "Try different filters"}</p>
                {items.length === 0 && <Button onClick={() => { resetForm(); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-2" /> Create Item</Button>}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                {filteredByFolderItems.map(item => {
                  const typeConfig = getItemTypeConfig(item.item_type);
                  const rarityConfig = getRarityConfig(item.rarity);
                  const TypeIcon = typeConfig.icon;
                  return (
                    <EntityContextMenu
                      key={item.id}
                      onOpen={() => setSelectedItem(item)}
                      onRename={() => openEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      folders={folders}
                      currentFolderId={item.folder_id}
                      onMoveFolder={(fid) => moveItemToFolder(item.id, fid)}
                      pinPath="items"
                      pinLabel={`Item: ${item.name}`}
                      entityName={item.name}
                    >
                    <Card className={`bg-card border-border rounded-sm entity-card cursor-pointer hover:border-primary/30 transition-colors ${item.equipped ? 'border-l-2 border-l-primary' : ''} relative ${multi.isSelected(item.id) ? "ring-2 ring-primary" : ""}`} data-testid={`item-card-${item.id}`} onClick={() => multi.selectMode ? multi.toggle(item.id) : setSelectedItem(item)} draggable onDragStart={e => e.dataTransfer.setData("entityId", item.id)}>
                      <FolderColorStrip entityId={item.id} folderId={item.folder_id} folders={folders} onFilter={folderCrud.setCurrentFolderId} testIdPrefix="item" />
                      {multi.selectMode && <SelectCheckbox checked={multi.isSelected(item.id)} onChange={() => multi.toggle(item.id)} />}
                      <CardContent className="p-4">
                        {item.image_path && (
                          <div className="w-full h-32 rounded-sm overflow-hidden mb-3 bg-muted/20">
                            <img src={`${API}/files/${item.image_path}`} alt={item.name} className="w-full h-full object-cover" crossOrigin="anonymous" data-testid={`item-image-${item.id}`} />
                          </div>
                        )}
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ${item.equipped ? 'bg-primary/20' : 'bg-muted/30'}`}>
                              <TypeIcon className={`w-5 h-5 ${item.equipped ? 'text-primary' : 'text-primary'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-serif font-bold text-lg leading-tight">{item.name}</h3>
                                {item.quantity > 1 && <span className="text-[10px] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-sm" data-testid={`item-qty-${item.id}`}>×{item.quantity}</span>}
                                {item.equipped && <span className="text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm" data-testid={`equipped-badge-${item.id}`}>Equipped</span>}
                                {item.in_party_loot && <span className="text-[10px] font-semibold uppercase tracking-wider text-green-300 bg-green-500/10 border border-green-500/30 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5" data-testid={`party-badge-${item.id}`}><Users className="w-2.5 h-2.5" />Party</span>}
                              </div>
                              <div className="flex items-center gap-2 text-xs mt-0.5 flex-wrap">
                                <span className="text-muted-foreground">{typeConfig.label}</span>
                                <span>·</span>
                                <span className={rarityConfig.color}>{rarityConfig.label}</span>
                                {item.weight > 0 && <><span>·</span><span className="text-muted-foreground flex items-center gap-0.5"><Weight className="w-3 h-3" />{item.weight}lb{item.quantity > 1 ? ` ea` : ``}</span></>}
                              </div>
                            </div>
                          </div>
                          <VisibilityToggle entityType="items" entityId={item.id} currentVisibility={item.visibility} onUpdate={fetchData} />
                        </div>
                        {item.properties && (
                          <p className="text-xs font-mono bg-muted/20 rounded-sm px-2 py-1 mb-2 text-muted-foreground">{item.properties}</p>
                        )}
                        {item.character_id && getCharName(item.character_id) && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                            <User className="w-3 h-3" /> Held by {getCharName(item.character_id)}
                          </div>
                        )}
                        {item.location_id && getLocName(item.location_id) && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                            <MapPin className="w-3 h-3" /> Found at {getLocName(item.location_id)}
                          </div>
                        )}
                        {item.description && <div className="text-sm text-muted-foreground line-clamp-2 mt-2"><RichTextDisplay content={item.description} /></div>}
                        <div className="flex gap-1 justify-end mt-3" onClick={(e) => e.stopPropagation()}>
                          {item.character_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleEquip(item)}
                              className={item.equipped ? "text-primary" : "text-muted-foreground"}
                              title={item.equipped ? "Unequip" : "Equip"}
                              data-testid={`equip-item-${item.id}`}
                            >
                              {item.equipped ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePartyLootForItem(item)}
                            className={item.in_party_loot ? "text-green-400" : "text-muted-foreground"}
                            title={item.in_party_loot ? "Remove from party loot" : "Send to party loot"}
                            data-testid={`toggle-party-loot-${item.id}`}
                          >
                            <Users className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`}><Edit className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-item-${item.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </CardContent>
                    </Card>
                    </EntityContextMenu>
                  );
                })}
              </div>
            )}
              </div>
            </div>
          </TabsContent>

          {/* ==================== PARTY LOOT POOL TAB ==================== */}
          <TabsContent value="party">
            <PartyLootPanel
              loot={partyLoot}
              loading={partyLootLoading}
              characters={characters}
              onTogglePartyLoot={togglePartyLootForItem}
              onAssignToCharacter={assignPartyItemToCharacter}
              onSaveCurrency={updatePartyCurrency}
              onSaveNotes={updatePartyNotes}
              onEditItem={openEdit}
            />
          </TabsContent>

          {/* ==================== ITEMS LIBRARY TAB ==================== */}
          <TabsContent value="library">
            <TierGate feature="Items Library" description="Browse 197 pre-built D&D items from the SRD — weapons, armour, potions, wondrous items, and more. Add them directly to your campaign inventory.">
              {srdLoading ? (
                <div className="flex justify-center py-20"><div className="spinner" /></div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={srdSearch}
                        onChange={(e) => setSrdSearch(e.target.value)}
                        placeholder="Search items..."
                        className="pl-10 bg-input border-border rounded-sm"
                        data-testid="srd-item-search"
                      />
                    </div>
                    <Select value={srdFilterType} onValueChange={setSrdFilterType}>
                      <SelectTrigger className="w-[150px] bg-input border-border rounded-sm" data-testid="srd-item-filter-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="all">All Types</SelectItem>
                        {SRD_ITEM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={srdFilterRarity} onValueChange={setSrdFilterRarity}>
                      <SelectTrigger className="w-[140px] bg-input border-border rounded-sm" data-testid="srd-item-filter-rarity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="all">All Rarities</SelectItem>
                        {RARITIES.map(r => <SelectItem key={r.value} value={r.value}><span className={r.color}>{r.label}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={srdFilterMagic} onValueChange={setSrdFilterMagic}>
                      <SelectTrigger className="w-[140px] bg-input border-border rounded-sm" data-testid="srd-item-filter-magic">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="all">All Items</SelectItem>
                        <SelectItem value="magical"><span className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-violet-400" /> Magical</span></SelectItem>
                        <SelectItem value="mundane">Non-Magical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <p className="text-xs text-muted-foreground mb-4">
                    {filteredSrd.length} of {srdItems.length} items &mdash; Click "Add" to copy to your campaign inventory
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredSrd.map(item => {
                      const isAdded = addedItems.has(item.id);
                      const isAdding = addingItem === item.id;
                      const rarityConfig = getRarityConfig(item.rarity);
                      return (
                        <Card key={item.id} className="bg-card border-border rounded-sm" data-testid={`srd-item-${item.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-serif text-base text-foreground truncate">{item.name}</h4>
                                  {item.magical && <Sparkles className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />}
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                                  <span className="capitalize">{item.item_type}</span>
                                  <span className={rarityConfig.color}>{rarityConfig.label}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{item.description}</p>
                              </div>
                              <Button
                                variant={isAdded ? "ghost" : "outline"}
                                size="sm"
                                className={`h-8 rounded-sm flex-shrink-0 ${isAdded ? "text-green-400" : "border-border"}`}
                                disabled={isAdding}
                                onClick={() => addSrdItem(item)}
                                data-testid={`add-srd-item-${item.id}`}
                              >
                                {isAdding ? (
                                  <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                ) : isAdded ? (
                                  <><Check className="w-3.5 h-3.5 mr-1" /> Added</>
                                ) : (
                                  <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>
                                )}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </TierGate>
          </TabsContent>
        </Tabs>

        {/* Item Detail SlidePanel */}
        <SlidePanel open={!!selectedItem} onClose={() => setSelectedItem(null)} title={selectedItem?.name || "Item"}
          items={filtered} currentIndex={selectedItem ? filtered.findIndex(i => i.id === selectedItem.id) : -1}
          onNavigate={(i) => setSelectedItem(filtered[i])}
        >
          {selectedItem && (() => {
            const tc = getItemTypeConfig(selectedItem.item_type);
            const rc = getRarityConfig(selectedItem.rarity);
            const TypeIcon = tc.icon;
            return (
              <div data-testid="item-detail-panel">
                {selectedItem.image_path && (
                  <div className="h-48 w-full rounded-sm overflow-hidden mb-4 bg-muted/20">
                    <img src={`${API}/files/${selectedItem.image_path}`} alt={selectedItem.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${selectedItem.equipped ? 'bg-primary/20' : 'bg-muted/30'}`}>
                    <TypeIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{tc.label}</span>
                      <span className={`text-sm ${rc.color}`}>{rc.label}</span>
                    </div>
                    {selectedItem.equipped && (
                      <span className="text-xs font-semibold text-primary">Currently Equipped</span>
                    )}
                  </div>
                </div>

                {selectedItem.properties && (
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Properties</h4>
                    <p className="text-sm font-mono bg-muted/20 rounded-sm px-2 py-1">{selectedItem.properties}</p>
                  </div>
                )}

                {selectedItem.character_id && getCharName(selectedItem.character_id) && (
                  <div className="flex items-center gap-1.5 text-sm text-foreground/80 mb-2">
                    <User className="w-3.5 h-3.5 text-primary" /> Held by {getCharName(selectedItem.character_id)}
                  </div>
                )}
                {selectedItem.location_id && getLocName(selectedItem.location_id) && (
                  <div className="flex items-center gap-1.5 text-sm text-foreground/80 mb-4">
                    <MapPin className="w-3.5 h-3.5 text-primary" /> Found at {getLocName(selectedItem.location_id)}
                  </div>
                )}

                {selectedItem.description && (
                  <div className="mb-4">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Description</h4>
                    <div className="text-sm text-foreground/80 prose-sm"><RichTextDisplay content={selectedItem.description} /></div>
                  </div>
                )}

                <div className="pt-4 border-t border-border mb-4">
                  <RelatedEntitiesPanel entityType="items" entityId={selectedItem.id} />
                </div>

                <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                  <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => { setSelectedItem(null); openEdit(selectedItem); }} data-testid="item-detail-edit">
                    <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => generateItemImage(selectedItem.id)} data-testid="item-generate-image">
                    <Wand2 className="w-3.5 h-3.5 mr-1.5" /> AI Image
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedItem.id)} data-testid="item-detail-delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })()}
        </SlidePanel>
      </div>
      <BulkActionBar count={multi.count} folders={folders} onBulkMove={bulkMoveItems} onBulkDelete={bulkDeleteItems} onClear={multi.clearAll} />
    </Layout>
  );
};

export default ItemsPage;

// ============================================================================
// PartyLootPanel — shared currency pool + party-loot items + assign back to PC
// ============================================================================
const PARTY_COINS = [
  { key: "pp", label: "PP", color: "text-cyan-200" },
  { key: "gp", label: "GP", color: "text-yellow-300" },
  { key: "ep", label: "EP", color: "text-emerald-300" },
  { key: "sp", label: "SP", color: "text-zinc-200" },
  { key: "cp", label: "CP", color: "text-orange-300" },
];

const PartyLootPanel = ({ loot, loading, characters, onTogglePartyLoot, onAssignToCharacter, onSaveCurrency, onSaveNotes, onEditItem }) => {
  const [draftCoins, setDraftCoins] = useState(null);
  const [draftNotes, setDraftNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const notesTimer = useState({ current: null })[0];

  useEffect(() => {
    if (loot) {
      setDraftCoins(loot.currency || { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 });
      setDraftNotes(loot.notes || "");
    }
  }, [loot?.updated_at, loot?.items?.length]);

  const setCoin = (k, v) => {
    const n = Math.max(0, parseInt(v || 0, 10) || 0);
    const next = { ...(draftCoins || {}), [k]: n };
    setDraftCoins(next);
    onSaveCurrency(next);
  };
  const adjust = (k, delta) => setCoin(k, (draftCoins?.[k] || 0) + delta);

  const onNotesChange = (val) => {
    setDraftNotes(val);
    setSavingNotes(true);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      await onSaveNotes(val);
      setSavingNotes(false);
    }, 700);
  };

  const gpTotal = !draftCoins ? 0 : (
    (draftCoins.pp || 0) * 10 +
    (draftCoins.gp || 0) +
    (draftCoins.ep || 0) * 0.5 +
    (draftCoins.sp || 0) * 0.1 +
    (draftCoins.cp || 0) * 0.01
  );

  if (loading) return <p className="text-muted-foreground text-sm">Loading party loot…</p>;
  if (!loot) return null;

  return (
    <div className="space-y-6" data-testid="party-loot-panel">
      {/* Currency pool */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-serif text-foreground flex items-center gap-2"><Coins className="w-5 h-5 text-yellow-400" /> Shared Currency</h2>
            <span className="text-xs text-muted-foreground" data-testid="party-gp-total">≈ {gpTotal.toFixed(2)} gp</span>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {PARTY_COINS.map(c => (
              <div key={c.key} className="rounded-sm border border-border bg-muted/20 p-2" data-testid={`party-currency-${c.key}`}>
                <div className={`text-[11px] uppercase tracking-wider font-bold ${c.color} text-center mb-1`}>{c.label}</div>
                <Input type="number" min="0" value={draftCoins?.[c.key] ?? 0} onChange={(e) => setCoin(c.key, e.target.value)} className="h-7 text-center text-sm bg-input border-border font-mono" data-testid={`party-currency-${c.key}-input`} />
                <div className="flex gap-1 mt-1">
                  <button onClick={() => adjust(c.key, -1)} className="flex-1 text-[10px] py-0.5 rounded-sm bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`party-currency-${c.key}-down`}>−1</button>
                  <button onClick={() => adjust(c.key, 1)} className="flex-1 text-[10px] py-0.5 rounded-sm bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`party-currency-${c.key}-up`}>+1</button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-serif text-foreground">Pool Notes</h2>
            <span className="text-[10px] text-muted-foreground">{savingNotes ? "Saving…" : "Saved"}</span>
          </div>
          <textarea
            value={draftNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Splits agreed, debts owed, party rules for loot… anyone in the campaign can edit."
            className="w-full h-20 text-sm bg-input border border-border rounded-sm p-2 resize-y focus:outline-none focus:border-primary"
            data-testid="party-loot-notes"
          />
        </CardContent>
      </Card>

      {/* Items */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-serif text-foreground flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Communal Items <span className="text-sm text-muted-foreground">({loot.items.length})</span></h2>
            <span className="text-xs text-muted-foreground flex items-center gap-1"><Weight className="w-3.5 h-3.5" /> {loot.total_weight} lb total</span>
          </div>
          {loot.items.length === 0 ? (
            <div className="text-center py-10">
              <Users className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No party loot yet. Click the <Users className="w-3 h-3 inline" /> badge on any item to send it to the shared pool.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="party-loot-items">
              {loot.items.map((item) => (
                <div key={item.id} className="flex items-center gap-3 bg-muted/20 border border-border rounded-sm p-3" data-testid={`party-item-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground truncate">{item.name}</span>
                      {item.quantity > 1 && <span className="text-[10px] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-sm">×{item.quantity}</span>}
                      {item.weight > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Weight className="w-2.5 h-2.5" />{item.weight}lb{item.quantity > 1 ? ` ea` : ``}</span>}
                    </div>
                    {item.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{(item.description || "").replace(/<[^>]+>/g, "")}</p>}
                  </div>
                  {characters.length > 0 && (
                    <Select onValueChange={(v) => v && onAssignToCharacter(item.id, v)}>
                      <SelectTrigger className="h-8 w-44 text-xs bg-input border-border" data-testid={`assign-item-${item.id}`}>
                        <SelectValue placeholder="Assign to…" />
                      </SelectTrigger>
                      <SelectContent>
                        {characters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onEditItem(item)} data-testid={`party-edit-${item.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => onTogglePartyLoot(item)} className="text-amber-400" title="Remove from party loot" data-testid={`party-remove-${item.id}`}><Users className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
